import React, { useMemo, useRef } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import { StepShell, HelpedBlock, Field, KPI } from '../atoms';

// EventCreation is the promoter-side form for the EUR-denominated marketplace.
// Force € regardless of the viewer's display-currency preference: the booking
// transaction itself is in EUR, so showing a different symbol here is misleading.
const CURRENCY_SYMBOL = '€';

const Step3 = () => {
  const { t } = useTranslation();
  const {
    register,
    control,
    formState: { errors },
    watch,
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({ control, name: 'spaces' });

  const rowRefs = useRef([]);
  const spacesValues = watch('spaces') || [];

  const currencySymbol = CURRENCY_SYMBOL;
  const pricePlaceholder = t('event_create.price_placeholder') || 'Enter price';

  const blockNumChars = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
  };

  const asNumberOrUndef = (v) => {
    if (v === '' || v === null || typeof v === 'undefined') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const asIntOrUndef = (v) => {
    if (v === '' || v === null || typeof v === 'undefined') return undefined;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : undefined;
  };

  const fieldLabel = useMemo(
    () => ({
      name: t('event_create.space_name') || 'Name',
      size: `${t('event_create.size') || 'Size'} (m²)`,
      description: t('event_create.description') || 'Description',
      quantity: t('event_create.available') || 'Quantity',
      price: t('event_create.price_no_taxes') || 'Price (excl. taxes)',
    }),
    [t]
  );

  const scrollToSpace = (index) => {
    const el = rowRefs.current[index];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const firstControl = el.querySelector('input, textarea, button');
    if (firstControl?.focus) firstControl.focus();
  };

  const prettifyMessage = (index, key, errObj) => {
    if (!errObj) return null;
    const rawMsg = String(errObj?.message || '');
    const errType = errObj?.type;
    const value = spacesValues?.[index]?.[key];
    const looksLikeNaN =
      rawMsg.includes('NaN') ||
      rawMsg.includes('must be a `number` type') ||
      errType === 'typeError';

    if (looksLikeNaN) {
      if (value === '' || value === undefined || value === null) {
        return (
          t('event_create.validation.required_field', {
            field: fieldLabel[key],
          }) || `${fieldLabel[key]} is required.`
        );
      }
      return (
        t('event_create.validation.invalid_number', {
          field: fieldLabel[key],
        }) || `${fieldLabel[key]} must be a valid number.`
      );
    }

    if (errType === 'required' || /required/i.test(rawMsg)) {
      return (
        t('event_create.validation.required_field', {
          field: fieldLabel[key],
        }) || `${fieldLabel[key]} is required.`
      );
    }

    if (errType === 'min' || /must be.*greater|at least|min/i.test(rawMsg)) {
      return (
        t('event_create.validation.non_negative', { field: fieldLabel[key] }) ||
        `${fieldLabel[key]} must be 0 or greater.`
      );
    }

    if (
      key === 'quantity' &&
      (errType === 'integer' || /integer|whole number/i.test(rawMsg))
    ) {
      return (
        t('event_create.validation.whole_number', { field: fieldLabel[key] }) ||
        `${fieldLabel[key]} must be a whole number.`
      );
    }

    return rawMsg
      .replace(/^spaces\[\d+\]\./, '')
      .replace(/`/g, '')
      .trim();
  };

  const buildErrorGroups = () => {
    const spacesErrors = errors?.spaces;
    const groups = [];
    let totalIssues = 0;

    if (Array.isArray(spacesErrors)) {
      spacesErrors.forEach((spaceErr, index) => {
        if (!spaceErr) return;
        const keys = Object.keys(spaceErr);
        const issues = keys
          .map((k) => {
            const msg = prettifyMessage(index, k, spaceErr?.[k]);
            if (!msg) return null;
            return { key: k, label: fieldLabel[k] || k, message: msg };
          })
          .filter(Boolean);

        if (issues.length) {
          totalIssues += issues.length;
          groups.push({ index, issues });
        }
      });
    }

    const arrayLevelMessage =
      (typeof spacesErrors === 'object' && spacesErrors?.message) ||
      errors?.spaces?.message ||
      null;

    return { groups, totalIssues, arrayLevelMessage };
  };

  const { groups, totalIssues, arrayLevelMessage } = buildErrorGroups();

  // KPIs
  const totalSpaces = spacesValues.reduce(
    (sum, s) => sum + (Number(s.quantity) || 0),
    0
  );
  const totalRevenue = spacesValues.reduce(
    (sum, s) => sum + (Number(s.quantity) || 0) * (Number(s.price) || 0),
    0
  );
  const totalArea = spacesValues.reduce(
    (sum, s) => sum + (Number(s.quantity) || 0) * (Number(s.size) || 0),
    0
  );

  const ErrorPanel = () => {
    const noSpacesError = errors?.spaces && !fields?.length;
    if (!totalIssues && !arrayLevelMessage && !noSpacesError) return null;

    const headerText =
      t('event_create.validation.fix_following') ||
      'Please fix the following before continuing:';
    const issuesText =
      totalIssues > 0
        ? t('event_create.validation.issues_found', { count: totalIssues }) ||
          `${totalIssues} issue(s) found.`
        : null;

    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-rose-600 mt-0.5" />
          <div className="min-w-0">
            <p className="font-bold text-rose-800 text-[13px]">{headerText}</p>
            {issuesText && (
              <p className="text-[12px] text-rose-700 mt-0.5">{issuesText}</p>
            )}
          </div>
        </div>

        <div className="mt-3 space-y-2">
          {arrayLevelMessage && (
            <div className="rounded-xl border border-rose-200 bg-white/60 p-3 text-[12.5px] text-rose-800">
              {arrayLevelMessage}
            </div>
          )}
          {noSpacesError && (
            <div className="rounded-xl border border-rose-200 bg-white/60 p-3 text-[12.5px] text-rose-800">
              {t('event_create.no_space') || 'Please add at least one space.'}
            </div>
          )}
          {groups.map(({ index, issues }) => (
            <div
              key={`space-err-${index}`}
              className="rounded-xl border border-rose-200 bg-white/60 p-3"
            >
              <button
                type="button"
                onClick={() => scrollToSpace(index)}
                className="w-full text-left flex items-center justify-between gap-3"
              >
                <span className="font-bold text-rose-800 text-[12.5px]">
                  {t('event_create.place') + ' ' + (index + 1)}
                </span>
                <span className="text-[11px] text-rose-700 underline">
                  {t('common.jump_to') || 'Jump to'}
                </span>
              </button>
              <ul className="mt-2 ml-5 list-disc space-y-1 text-[12.5px] text-rose-700">
                {issues.map((it) => (
                  <li key={`${index}-${it.key}`}>
                    <span className="font-bold">{it.label}:</span> {it.message}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <StepShell
      eyebrow={t('event_create.step3_eyebrow', 'Step 03 / Spaces')}
      title={t('event_create.step3_title', 'What can brands book?')}
      lede={
        <>
          {t(
            'event_create.step3_lede',
            'A space is the unit a brand pays for — a stand, a pitch, a corner. Define each type with size, quantity, and price, excluding VAT.'
          )}{' '}
          <strong className="font-semibold text-gray-700">
            {t(
              'event_create.step3_price_note',
              'Brands pay this price plus VAT and the t2b fee.'
            )}
          </strong>
        </>
      }
    >
      <HelpedBlock sectionKey="spaces">
      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPI
          label={t('event_create.kpi_units', 'Bookable units')}
          value={totalSpaces}
          suffix={` ${t('event_create.kpi_units_suffix', 'stands')}`}
        />
        <KPI
          label={t('event_create.kpi_area', 'Total area')}
          value={totalArea}
          suffix=" m²"
        />
        <KPI
          label={t('event_create.kpi_revenue', 'Max revenue')}
          value={totalRevenue.toLocaleString('en-GB')}
          prefix={currencySymbol}
        />
      </div>

      <ErrorPanel />

      {/* Space cards */}
      <div className="space-y-4">
        {fields.map((item, index) => {
          const e = errors?.spaces?.[index] || {};
          const lineTotal =
            (Number(spacesValues?.[index]?.quantity) || 0) *
            (Number(spacesValues?.[index]?.price) || 0);

          return (
            <div
              key={item.id}
              ref={(el) => (rowRefs.current[index] = el)}
              className="bg-white rounded-2xl border border-line shadow-card overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-line gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="w-8 h-8 grid place-items-center rounded-lg bg-brand text-ink-strong text-[12px] font-extrabold tabular-nums shrink-0">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <input
                      {...register(`spaces.${index}.name`)}
                      className="text-[15px] font-extrabold text-ink-strong tracking-tight bg-transparent border-0 outline-none w-full focus:bg-paper-2 px-2 py-0.5 -ml-2 rounded"
                      placeholder={t('event_create.space_name_placeholder')}
                      aria-invalid={!!e?.name}
                    />
                    <p className="text-[11px] text-muted">
                      {Number(spacesValues?.[index]?.quantity) || 0} ×{' '}
                      {Number(spacesValues?.[index]?.size) || 0}m² @{' '}
                      {currencySymbol}
                      {(
                        Number(spacesValues?.[index]?.price) || 0
                      ).toLocaleString('en-GB')}{' '}
                      ={' '}
                      <span className="font-bold text-ink-strong tabular-nums">
                        {currencySymbol}
                        {lineTotal.toLocaleString('en-GB')}
                      </span>{' '}
                      {t('event_create.max', 'max')}
                    </p>
                  </div>
                </div>
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="w-9 h-9 grid place-items-center rounded-full text-gray-400 hover:bg-rose-50 hover:text-rose-600 transition"
                    aria-label={t('common.remove') || 'Remove'}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Body */}
              <div className="px-5 py-5 grid grid-cols-12 gap-4">
                <Field
                  className="col-span-12 md:col-span-4"
                  label={t('event_create.size')}
                  error={prettifyMessage(index, 'size', e?.size)}
                >
                  <div className="relative">
                    <input
                      type="number"
                      {...register(`spaces.${index}.size`, {
                        setValueAs: asNumberOrUndef,
                      })}
                      className="field-input pr-12"
                      placeholder={t('event_create.size_placeholder')}
                      min="0"
                      onKeyDown={blockNumChars}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-muted">
                      m²
                    </span>
                  </div>
                </Field>

                <Field
                  className="col-span-6 md:col-span-4"
                  label={t('event_create.available')}
                  error={prettifyMessage(index, 'quantity', e?.quantity)}
                >
                  <input
                    type="number"
                    {...register(`spaces.${index}.quantity`, {
                      setValueAs: asIntOrUndef,
                    })}
                    className="field-input"
                    placeholder={t('event_create.available_placeholder')}
                    min="0"
                    step="1"
                    onKeyDown={blockNumChars}
                  />
                </Field>

                <Field
                  className="col-span-6 md:col-span-4"
                  label={t('event_create.price_no_taxes')}
                  error={prettifyMessage(index, 'price', e?.price)}
                >
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[12px] font-bold text-muted">
                      {currencySymbol}
                    </span>
                    <input
                      type="number"
                      {...register(`spaces.${index}.price`, {
                        setValueAs: asNumberOrUndef,
                      })}
                      className="field-input pl-9"
                      placeholder={pricePlaceholder}
                      min="0"
                      step="0.01"
                      onKeyDown={blockNumChars}
                    />
                  </div>
                </Field>

                <Field
                  className="col-span-12"
                  label={t('event_create.description')}
                  hint={`${(spacesValues?.[index]?.description || '').length}/100 — ${t('event_create.description_hint', 'what makes this space different')}`}
                  error={prettifyMessage(index, 'description', e?.description)}
                >
                  <textarea
                    rows={2}
                    maxLength={100}
                    {...register(`spaces.${index}.description`)}
                    className="field-input resize-none"
                    placeholder={t('event_create.description_placeholder')}
                  />
                </Field>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add another */}
      <button
        type="button"
        onClick={() =>
          append({
            name: '',
            size: '',
            description: '',
            quantity: '',
            price: '',
          })
        }
        className="w-full py-5 rounded-2xl border border-dashed border-line bg-white hover:bg-paper-2 text-gray-500 hover:text-ink-strong grid place-items-center transition group"
      >
        <span className="inline-flex items-center gap-2 text-[13px] font-bold">
          <span className="w-7 h-7 grid place-items-center rounded-full bg-brand text-ink-strong group-hover:scale-110 transition">
            <PlusIcon className="w-4 h-4" />
          </span>
          {t('event_create.add_space_type', 'Add another space type')}
        </span>
      </button>

      <p className="text-[11px] text-muted">
        {t(
          'event_create.price_excl_taxes_note',
          'All prices are excluding taxes.'
        )}
      </p>
      </HelpedBlock>
    </StepShell>
  );
};

export default Step3;
